import React, { useState, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
    HiOutlineX, HiOutlineUpload, HiOutlineClipboardList, HiOutlineChevronLeft,
    HiOutlineCheckCircle, HiOutlineExclamationCircle
} from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';

// Odoo-style import: pick any Excel/CSV (or paste), map its columns onto our
// three fields by hand, Test (a dry run against the current list — nothing is
// written), then Import. Everything up to Import is client-side.

const FIELDS = [
    { id: 'code', label: 'Code', required: true },
    { id: 'description', label: 'Description' },
    { id: 'uom', label: 'Unit' }
];
const SKIP = '';

// Header names we auto-map. Lower-cased, punctuation stripped before compare.
const ALIASES = {
    code: ['code', 'itemcode', 'itemno', 'itemnumber', 'item', 'productcode', 'sku', 'partno', 'partnumber', 'barcode', 'no', 'itemid', 'articlecode'],
    description: ['description', 'desc', 'itemdescription', 'itemname', 'name', 'productname', 'product', 'details'],
    uom: ['unit', 'uom', 'units', 'unitofmeasure', 'unitofmeasurement', 'measure', 'packing', 'pack']
};

const PREVIEW_ROWS = 50;
const IMPORT_CHUNK = 500;

const squash = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const cell = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();
const normalizeCode = (c) => cell(c).toUpperCase();

function guessField(header) {
    const h = squash(header);
    if (!h) return SKIP;
    for (const [field, names] of Object.entries(ALIASES)) {
        if (names.includes(h)) return field;
    }
    for (const [field, names] of Object.entries(ALIASES)) {
        if (names.some((n) => n.length > 3 && h.includes(n))) return field;
    }
    return SKIP;
}

// Pasted text: Excel copies as tabs; otherwise whichever of ; or , is more common.
function parsePasted(text) {
    const lines = text.replace(/\r\n?/g, '\n').split('\n').filter((l) => l.trim());
    if (!lines.length) return [];
    const sample = lines.slice(0, 20).join('\n');
    const delim = sample.includes('\t') ? '\t'
        : (sample.split(';').length > sample.split(',').length ? ';' : ',');
    return lines.map((line) => splitDelimited(line, delim));
}

function splitDelimited(line, delim) {
    if (delim === '\t') return line.split('\t');
    const out = [];
    let cur = '';
    let quoted = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (quoted) {
            if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
            else if (ch === '"') quoted = false;
            else cur += ch;
        } else if (ch === '"') quoted = true;
        else if (ch === delim) { out.push(cur); cur = ''; }
        else cur += ch;
    }
    out.push(cur);
    return out;
}

// Drop fully empty rows/columns and pad every row to the same width.
function tidy(rows) {
    const nonEmpty = rows.filter((r) => r.some((c) => cell(c)));
    const width = Math.max(0, ...nonEmpty.map((r) => r.length));
    const used = [];
    for (let c = 0; c < width; c++) {
        if (nonEmpty.some((r) => cell(r[c]))) used.push(c);
    }
    return nonEmpty.map((r) => used.map((c) => cell(r[c])));
}

function looksLikeHeader(rows) {
    if (rows.length < 2) return false;
    return rows[0].some((h) => guessField(h) !== SKIP);
}

function autoMapping(rows, hasHeader) {
    const width = rows[0]?.length || 0;
    const mapping = Array(width).fill(SKIP);
    if (hasHeader) {
        const taken = new Set();
        rows[0].forEach((h, i) => {
            const f = guessField(h);
            if (f && !taken.has(f)) { mapping[i] = f; taken.add(f); }
        });
    }
    // No header (or nothing recognised): Code / Description / Unit left-to-right.
    if (!mapping.includes('code')) {
        FIELDS.forEach((f, i) => { if (i < width && !mapping.includes(f.id)) mapping[i] = f.id; });
    }
    return mapping;
}

function FrozenImportWizard({ existingItems, onClose, onImported }) {
    const [step, setStep] = useState('source'); // source | map | test | done
    const [pasteText, setPasteText] = useState('');
    const [fileName, setFileName] = useState('');
    const [workbook, setWorkbook] = useState(null);
    const [sheetName, setSheetName] = useState('');
    const [rawRows, setRawRows] = useState([]);
    const [hasHeader, setHasHeader] = useState(true);
    const [mapping, setMapping] = useState([]);
    const [splitCode, setSplitCode] = useState(false);
    const [filter, setFilter] = useState('all');
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInput = useRef(null);

    const loadRows = (rows, name) => {
        const clean = tidy(rows);
        if (!clean.length) { setError('No data found.'); return; }
        const header = looksLikeHeader(clean);
        setRawRows(clean);
        setHasHeader(header);
        setMapping(autoMapping(clean, header));
        setSplitCode(false);
        setFileName(name);
        setError('');
        setStep('map');
    };

    const readSheet = (wb, name) => {
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: false, defval: '' });
        setSheetName(name);
        return rows;
    };

    const onFile = async (file) => {
        if (!file) return;
        try {
            const buf = await file.arrayBuffer();
            // raw:false above keeps codes as the text Excel shows (leading zeros etc.)
            const wb = XLSX.read(buf, { type: 'array', cellDates: false });
            setWorkbook(wb);
            loadRows(readSheet(wb, wb.SheetNames[0]), file.name);
        } catch (err) {
            console.error('Import read failed', err);
            setError('Could not read that file. Use .xlsx, .xls or .csv.');
        }
    };

    const onPaste = () => {
        if (!pasteText.trim()) return;
        setWorkbook(null);
        loadRows(parsePasted(pasteText), 'Pasted data');
    };

    const changeSheet = (name) => {
        const rows = tidy(readSheet(workbook, name));
        const header = looksLikeHeader(rows);
        setRawRows(rows);
        setHasHeader(header);
        setMapping(autoMapping(rows, header));
    };

    const toggleHeader = (on) => {
        setHasHeader(on);
        setMapping(autoMapping(rawRows, on));
    };

    const setColumnField = (col, field) => {
        setMapping((prev) => prev.map((f, i) => {
            if (i === col) return field;
            return field && f === field ? SKIP : f; // a field maps from one column only
        }));
    };

    const dataRows = useMemo(() => (hasHeader ? rawRows.slice(1) : rawRows), [rawRows, hasHeader]);
    const headers = useMemo(
        () => (rawRows[0] || []).map((h, i) => (hasHeader && h ? h : `Column ${i + 1}`)),
        [rawRows, hasHeader]
    );
    const codeCol = mapping.indexOf('code');
    const descCol = mapping.indexOf('description');
    const uomCol = mapping.indexOf('uom');

    // The mistake that broke the list before: code and description in one
    // cell ("12713 SADIA CHICKEN LIVER"). Offer to split when that's the case.
    const codeHasSpaces = useMemo(() => {
        if (codeCol < 0) return false;
        const vals = dataRows.map((r) => r[codeCol]).filter(Boolean);
        return vals.length > 0 && vals.filter((v) => /\s/.test(v)).length / vals.length > 0.5;
    }, [dataRows, codeCol]);

    const analysis = useMemo(() => {
        if (step !== 'test') return null;
        const existing = new Map(existingItems.map((i) => [normalizeCode(i.code), i]));
        const seen = new Map();
        const rows = dataRows.map((r, idx) => {
            let code = codeCol >= 0 ? r[codeCol] : '';
            let description = descCol >= 0 ? r[descCol] : '';
            const uom = uomCol >= 0 ? r[uomCol] : '';
            if (splitCode) {
                const m = code.match(/^(\S+)\s+(.+)$/);
                if (m) { code = m[1]; if (!description) description = m[2]; }
            }
            code = normalizeCode(code);
            const row = { line: idx + (hasHeader ? 2 : 1), code, description, uom };
            if (!code) return { ...row, status: 'error', message: 'Code is empty' };
            if (/\s/.test(code)) return { ...row, status: 'error', message: 'Code contains spaces — map description separately or tick "split"' };
            if (seen.has(code)) return { ...row, status: 'error', message: `Duplicate of line ${seen.get(code)}` };
            seen.set(code, row.line);
            const prev = existing.get(code);
            if (!prev) return { ...row, status: 'new' };
            const changed = (prev.description || '') !== description || (prev.uom || '') !== uom;
            return { ...row, status: changed ? 'update' : 'same', message: changed ? `was: ${prev.description || '—'} / ${prev.uom || '—'}` : '' };
        });
        const count = (s) => rows.filter((r) => r.status === s).length;
        return { rows, counts: { new: count('new'), update: count('update'), same: count('same'), error: count('error') } };
    }, [step, dataRows, codeCol, descCol, uomCol, splitCode, hasHeader, existingItems]);

    const runImport = async () => {
        const items = analysis.rows
            .filter((r) => r.status === 'new' || r.status === 'update')
            .map(({ code, description, uom }) => ({ code, description, uom }));
        if (!items.length) return;
        setImporting(true);
        setError('');
        try {
            for (let i = 0; i < items.length; i += IMPORT_CHUNK) {
                await axiosInstance.post('/api/frozen-pick/items/bulk', { items: items.slice(i, i + IMPORT_CHUNK) });
            }
            setResult({ added: analysis.counts.new, updated: analysis.counts.update, skipped: analysis.counts.same + analysis.counts.error });
            setStep('done');
            onImported();
        } catch (err) {
            setError(err.response?.data?.message || 'Import failed — nothing after the failed batch was saved.');
        } finally {
            setImporting(false);
        }
    };

    const visibleRows = analysis
        ? analysis.rows.filter((r) => filter === 'all' || r.status === filter)
        : [];

    const STEPS = [['source', 'Choose data'], ['map', 'Match columns'], ['test', 'Test & import']];
    const stepIndex = step === 'done' ? 3 : STEPS.findIndex(([s]) => s === step);

    return (
        <div className="fiw-overlay" onMouseDown={(e) => e.target === e.currentTarget && !importing && onClose()}>
            <div className="fiw-modal" role="dialog" aria-modal="true" aria-label="Import frozen items">
                <div className="fiw-head">
                    <h2>Import frozen items</h2>
                    <ol className="fiw-steps">
                        {STEPS.map(([id, label], i) => (
                            <li key={id} className={i < stepIndex ? 'done' : i === stepIndex ? 'current' : ''}>
                                <span>{i + 1}</span>{label}
                            </li>
                        ))}
                    </ol>
                    <button className="fiw-close" onClick={onClose} disabled={importing} aria-label="Close"><HiOutlineX /></button>
                </div>

                <div className="fiw-body">
                    {error && <div className="fiw-error"><HiOutlineExclamationCircle /> {error}</div>}

                    {step === 'source' && (
                        <div className="fiw-source">
                            <div
                                className={`fiw-drop${dragOver ? ' over' : ''}`}
                                onClick={() => fileInput.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={(e) => { e.preventDefault(); setDragOver(false); onFile(e.dataTransfer.files[0]); }}
                            >
                                <HiOutlineUpload className="fiw-drop-icon" />
                                <strong>Drop an Excel or CSV file here</strong>
                                <span>or click to choose · .xlsx, .xls, .csv · any column order</span>
                                <input
                                    ref={fileInput}
                                    type="file"
                                    accept=".xlsx,.xls,.csv,.txt"
                                    hidden
                                    onChange={(e) => { onFile(e.target.files[0]); e.target.value = ''; }}
                                />
                            </div>
                            <div className="fiw-or">or paste</div>
                            <div className="fiw-paste">
                                <textarea
                                    rows={6}
                                    value={pasteText}
                                    onChange={(e) => setPasteText(e.target.value)}
                                    placeholder={'Copy cells straight from Excel and paste here (tab, comma or ; separated)\n\n12713\tSADIA CHICKEN LIVER 450GMX20\tCTN\n12714\tSADIA CHICKEN FRANK (340GMX24)\tCTN'}
                                />
                                <button className="fpp-btn" onClick={onPaste} disabled={!pasteText.trim()}>
                                    <HiOutlineClipboardList /> Use pasted data
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'map' && (
                        <div className="fiw-map">
                            <div className="fiw-toolbar">
                                <span className="fiw-file">{fileName} · {dataRows.length} rows</span>
                                {workbook && workbook.SheetNames.length > 1 && (
                                    <label>Sheet
                                        <select value={sheetName} onChange={(e) => changeSheet(e.target.value)}>
                                            {workbook.SheetNames.map((n) => <option key={n}>{n}</option>)}
                                        </select>
                                    </label>
                                )}
                                <label className="fiw-check">
                                    <input type="checkbox" checked={hasHeader} onChange={(e) => toggleHeader(e.target.checked)} />
                                    First row is a header
                                </label>
                            </div>
                            <p className="fiw-hint">Match each column in your file to a field. Columns set to “Do not import” are ignored.</p>
                            <div className="fiw-map-list">
                                {headers.map((h, i) => {
                                    const samples = dataRows.slice(0, 3).map((r) => r[i]).filter(Boolean);
                                    return (
                                        <div key={i} className={`fiw-map-row${mapping[i] ? ' mapped' : ''}`}>
                                            <div className="fiw-map-src">
                                                <strong>{h}</strong>
                                                <span>{samples.length ? samples.join(' · ') : 'empty'}</span>
                                            </div>
                                            <span className="fiw-arrow">→</span>
                                            <select value={mapping[i] || SKIP} onChange={(e) => setColumnField(i, e.target.value)}>
                                                <option value={SKIP}>Do not import</option>
                                                {FIELDS.map((f) => <option key={f.id} value={f.id}>{f.label}{f.required ? ' *' : ''}</option>)}
                                            </select>
                                        </div>
                                    );
                                })}
                            </div>
                            {codeHasSpaces && (
                                <label className="fiw-check fiw-warn">
                                    <input type="checkbox" checked={splitCode} onChange={(e) => setSplitCode(e.target.checked)} />
                                    The Code column also contains the description (e.g. “12713 SADIA CHICKEN…”). Split it at the first space.
                                </label>
                            )}
                            {codeCol < 0 && <div className="fiw-error"><HiOutlineExclamationCircle /> Map one column to Code to continue.</div>}
                        </div>
                    )}

                    {step === 'done' && result && (
                        <div className="fiw-success">
                            <HiOutlineCheckCircle />
                            <div>
                                <strong>Import complete</strong>
                                <span>{result.added} added · {result.updated} updated · {result.skipped} skipped</span>
                            </div>
                        </div>
                    )}

                    {step === 'test' && analysis && (
                        <div className="fiw-test">
                            <p className="fiw-hint">
                                Test run — nothing has been saved yet. Only <b>New</b> and <b>Update</b> rows will be imported;
                                rows with errors are skipped. Items already on the list but not in this file are left alone.
                            </p>
                            <div className="fiw-chips">
                                {[['all', 'All', analysis.rows.length], ['new', 'New', analysis.counts.new], ['update', 'Update', analysis.counts.update], ['same', 'Unchanged', analysis.counts.same], ['error', 'Errors', analysis.counts.error]].map(([id, label, n]) => (
                                    <button key={id} className={`fiw-chip fiw-chip-${id}${filter === id ? ' active' : ''}`} onClick={() => setFilter(id)}>
                                        {label} <b>{n}</b>
                                    </button>
                                ))}
                            </div>
                            <div className="fiw-table-wrap">
                                <table className="fpp-table fiw-table">
                                    <thead><tr><th>Line</th><th>Code</th><th>Description</th><th>Unit</th><th>Result</th></tr></thead>
                                    <tbody>
                                        {visibleRows.slice(0, filter === 'error' ? undefined : PREVIEW_ROWS).map((r) => (
                                            <tr key={r.line} className={`fiw-row-${r.status}`}>
                                                <td>{r.line}</td>
                                                <td className="fiw-code">{r.code || '—'}</td>
                                                <td>{r.description}</td>
                                                <td>{r.uom}</td>
                                                <td>
                                                    <span className={`fiw-badge fiw-badge-${r.status}`}>
                                                        {{ new: 'New', update: 'Update', same: 'Unchanged', error: 'Error' }[r.status]}
                                                    </span>
                                                    {r.message && <small>{r.message}</small>}
                                                </td>
                                            </tr>
                                        ))}
                                        {visibleRows.length === 0 && <tr><td colSpan={5} className="fpp-empty-cell">Nothing here</td></tr>}
                                    </tbody>
                                </table>
                                {filter !== 'error' && visibleRows.length > PREVIEW_ROWS && (
                                    <div className="fiw-more">Showing first {PREVIEW_ROWS} of {visibleRows.length}</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="fiw-foot">
                    {step === 'map' && (
                        <>
                            <button className="fiw-ghost" onClick={() => setStep('source')}><HiOutlineChevronLeft /> Back</button>
                            <button className="fpp-btn" disabled={codeCol < 0 || !dataRows.length} onClick={() => { setFilter('all'); setStep('test'); }}>
                                Test
                            </button>
                        </>
                    )}
                    {step === 'test' && analysis && (
                        <>
                            <button className="fiw-ghost" onClick={() => setStep('map')} disabled={importing}><HiOutlineChevronLeft /> Back to columns</button>
                            <button
                                className="fpp-btn"
                                disabled={importing || analysis.counts.new + analysis.counts.update === 0}
                                onClick={runImport}
                            >
                                {importing ? 'Importing…' : `Import ${analysis.counts.new + analysis.counts.update} item(s)`}
                            </button>
                        </>
                    )}
                    {step === 'done' && <button className="fpp-btn" onClick={onClose}>Done</button>}
                </div>
            </div>
        </div>
    );
}

export default FrozenImportWizard;
